// Visueller Editor: Elemente per Pfad finden und Änderungen (Overrides) anwenden - ohne React-Knoten zu verschieben
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const isOwn = (el) => el.hasAttribute?.("data-at-added") || el.hasAttribute?.("data-at-ui");

const childIndex = (el) => {
  let i = 0;
  for (const c of el.parentElement.children) {
    if (c === el) return i;
    if (c.tagName === el.tagName && !isOwn(c)) i += 1;
  }
  return -1;
};

export const pathFor = (el) => {
  if (el.hasAttribute?.("data-at-added")) return `a:${el.getAttribute("data-at-added")}`;
  const parts = [];
  let cur = el;
  while (cur && cur !== document.body && cur.parentElement) {
    const tid = cur.getAttribute("data-testid");
    if (tid && document.querySelectorAll(`[data-testid="${CSS.escape(tid)}"]`).length === 1) {
      parts.unshift(`t:${tid}`);
      return parts.join("/");
    }
    parts.unshift(`${cur.tagName.toLowerCase()}:${childIndex(cur)}`);
    cur = cur.parentElement;
  }
  return parts.join("/");
};

export const findEl = (path) => {
  if (!path) return null;
  if (path.startsWith("a:")) return document.querySelector(`[data-at-added="${CSS.escape(path.slice(2))}"]`);
  let cur = document.body;
  for (const seg of path.split("/")) {
    if (seg.startsWith("t:")) {
      cur = document.querySelector(`[data-testid="${CSS.escape(seg.slice(2))}"]`);
    } else {
      const [tag, idx] = seg.split(":");
      cur = [...(cur?.children || [])].filter((c) => c.tagName.toLowerCase() === tag && !isOwn(c))[Number(idx)] || null;
    }
    if (!cur) return null;
  }
  return cur;
};

export const safeUrl = (u) => {
  const v = (u || "").trim();
  if (v.startsWith("https://") || (v.startsWith("/") && !v.startsWith("//"))) return v.startsWith("/api/") ? BACKEND_URL + v : v;
  return "";
};
const safeColor = (c) => (/^#[0-9a-fA-F]{3,8}$/.test(c || "") ? c : "");

export const textNodeOf = (el) => {
  if (el.children.length) return null;
  return [...el.childNodes].find((n) => n.nodeType === 3 && n.nodeValue.trim()) || null;
};

const ORIGINAL = "data-at-orig";
let editMode = false;
export const setEditMode = (v) => { editMode = v; };

const restoreText = (el) => {
  const t = [...el.childNodes].find((n) => n.nodeType === 3 && n.__atOrig !== undefined);
  if (t) { t.nodeValue = t.__atOrig; delete t.__atOrig; }
  if (el.hasAttribute("data-at-orig-src")) { el.setAttribute("src", el.getAttribute("data-at-orig-src")); el.removeAttribute("data-at-orig-src"); }
};

const applyOne = (el, ov) => {
  if (!el.hasAttribute(ORIGINAL)) el.setAttribute(ORIGINAL, "1");
  el.style.display = ov.hidden && !editMode ? "none" : "";
  el.style.opacity = ov.hidden && editMode ? "0.25" : "";
  el.style.translate = ov.dx || ov.dy ? `${Number(ov.dx) || 0}px ${Number(ov.dy) || 0}px` : "";
  const hasZ = ov.z !== undefined && ov.z !== null;
  const needPos = ov.dx || ov.dy || hasZ;
  if (needPos && !el.hasAttribute("data-at-pos") && getComputedStyle(el).position === "static") { el.style.position = "relative"; el.setAttribute("data-at-pos", "1"); }
  if (!needPos && el.hasAttribute("data-at-pos")) { el.style.position = ""; el.removeAttribute("data-at-pos"); }
  el.style.zIndex = hasZ ? String(Math.min(Math.max(Number(ov.z) || 0, -999), 999)) : ov.dx || ov.dy ? "5" : "";
  if (hasZ && el.parentElement && !el.parentElement.style.isolation) { el.parentElement.style.isolation = "isolate"; el.parentElement.setAttribute("data-at-iso", "1"); }
  el.style.order = ov.order !== undefined ? String(ov.order) : "";
  el.style.color = safeColor(ov.color);
  el.style.backgroundColor = safeColor(ov.bg);
  el.style.fontSize = ov.fontSize ? `${Math.min(Math.max(Number(ov.fontSize), 8), 160)}px` : "";
  el.style.fontWeight = ov.fontWeight || "";
  el.style.textAlign = ["left", "center", "right"].includes(ov.align) ? ov.align : "";
  el.style.width = ov.width ? `${Math.min(Math.max(Number(ov.width), 10), 2000)}px` : "";
  if (ov.text !== undefined) {
    const t = textNodeOf(el);
    if (t && t.nodeValue !== ov.text) {
      if (t.__atOrig === undefined) t.__atOrig = t.nodeValue;
      t.nodeValue = ov.text;
    }
  } else restoreText(el);
  if (ov.src !== undefined && el.tagName === "IMG") {
    const u = safeUrl(ov.src);
    if (u && el.getAttribute("src") !== u) {
      if (!el.hasAttribute("data-at-orig-src")) el.setAttribute("data-at-orig-src", el.getAttribute("src") || "");
      el.setAttribute("src", u); el.removeAttribute("srcset");
    }
  }
  if (ov.href !== undefined) {
    const u = safeUrl(ov.href);
    const a = el.closest("a") || el;
    if (u) a.setAttribute("data-at-href", u); else a.removeAttribute("data-at-href");
  }
};

const ensureFlexParent = (el) => {
  const p = el.parentElement;
  if (p && getComputedStyle(p).display === "block") {
    p.style.display = "flex";
    p.style.flexDirection = "column";
  }
};

export const parseTabs = (text) => {
  try {
    const t = JSON.parse(text || "[]");
    return Array.isArray(t) && t.length ? t.map((x) => ({ t: String(x.t ?? ""), c: String(x.c ?? "") })) : [{ t: "Reiter 1", c: "" }];
  } catch {
    return [{ t: "Reiter 1", c: "" }];
  }
};

const renderTabs = (el, text) => {
  const tabs = parseTabs(text);
  const current = Math.min(Number(el.getAttribute("data-at-tab") || 0), tabs.length - 1);
  el.replaceChildren();
  const bar = document.createElement("div");
  bar.className = "flex flex-wrap border-b-2 border-black";
  const body = document.createElement("div");
  body.className = "p-6 bg-white border border-t-0 border-[#D9D9D6] leading-relaxed";
  body.style.whiteSpace = "pre-line";
  tabs.forEach((tab, i) => {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = tab.t || `Reiter ${i + 1}`;
    b.className = `px-5 py-3 text-sm font-bold transition-colors duration-150 ${i === current ? "bg-black text-[#EFDF00]" : "bg-[#F2F2F2] text-black hover:bg-[#EFDF00]"}`;
    b.setAttribute("data-testid", `added-tab-${i}`);
    b.addEventListener("click", () => { el.setAttribute("data-at-tab", String(i)); renderTabs(el, text); });
    bar.appendChild(b);
  });
  body.textContent = tabs[current]?.c || "";
  el.append(bar, body);
  el.setAttribute("data-at-tabs-src", text || "");
};

const buildAdded = (a) => {
  let el;
  if (a.kind === "tabs") {
    el = document.createElement("div");
    el.className = "my-6 w-full";
    renderTabs(el, a.text);
  } else if (a.kind === "image") {
    el = document.createElement("img");
    const u = safeUrl(a.src);
    if (u) el.src = u;
    el.alt = "";
    el.style.maxWidth = "100%";
  } else if (a.kind === "button") {
    el = document.createElement("a");
    el.className = "inline-block bg-[#EFDF00] text-black font-bold px-6 py-3 my-2";
    el.textContent = a.text || "Knopf";
    const u = safeUrl(a.href);
    if (u) el.setAttribute("data-at-href", u);
    el.style.cursor = "pointer";
  } else {
    el = document.createElement(a.kind === "heading" ? "h2" : "p");
    el.className = a.kind === "heading" ? "text-3xl font-bold my-3" : "my-2 leading-relaxed";
    el.textContent = a.text || "Neuer Text";
  }
  el.setAttribute("data-at-added", a.id);
  return el;
};

// Wendet die Änderungen einer Seite an. Rückgabe: Anzahl gefundener Elemente
export const applyPage = (page) => {
  const els = page?.elements || {};
  const added = page?.added || [];
  const wanted = new Set(added.map((a) => a.id));
  document.querySelectorAll("[data-at-added]").forEach((n) => { if (!wanted.has(n.getAttribute("data-at-added"))) n.remove(); });
  for (const a of added) {
    const anchor = findEl(a.after);
    let el = document.querySelector(`[data-at-added="${CSS.escape(a.id)}"]`);
    if (!anchor || !anchor.parentElement) { el?.remove(); continue; }
    if (!el) {
      el = buildAdded(a);
      anchor.parentElement.insertBefore(el, anchor.nextSibling);
    } else if (a.kind === "tabs") {
      if (el.getAttribute("data-at-tabs-src") !== (a.text || "")) renderTabs(el, a.text);
    } else if (a.kind !== "image") {
      if (el.textContent !== (a.text || "")) el.textContent = a.text || "";
    }
    if (a.kind === "image") { const u = safeUrl(a.src); if (u && el.getAttribute("src") !== u) el.src = u; }
    if (a.kind === "button") { const u = safeUrl(a.href); if (u) el.setAttribute("data-at-href", u); }
  }
  document.querySelectorAll(`[${ORIGINAL}]`).forEach((n) => {
    if (!Object.keys(els).some((k) => findEl(k) === n)) {
      ["display", "translate", "order", "color", "backgroundColor", "fontSize", "fontWeight", "textAlign", "width", "zIndex", "opacity"].forEach((p) => { n.style[p] = ""; });
      if (n.hasAttribute("data-at-pos")) { n.style.position = ""; n.removeAttribute("data-at-pos"); }
      const par = n.parentElement;
      if (par?.hasAttribute("data-at-iso")) { par.style.isolation = ""; par.removeAttribute("data-at-iso"); }
      restoreText(n);
      n.removeAttribute(ORIGINAL);
    }
  });
  let found = 0;
  for (const [path, ov] of Object.entries(els)) {
    const el = findEl(path);
    if (!el) continue;
    found += 1;
    if (ov.order !== undefined) ensureFlexParent(el);
    applyOne(el, ov);
  }
  return found;
};
